import React, { useRef, useEffect } from 'react'
import { MessageSquare, SendHorizonal, ImagePlus, X, Check, Rocket, FileText } from 'lucide-react'
import styles from './ConversationInput.module.scss'
import draftPublishStyles from '../DraftPublishButton.module.scss'

const isEditableFocused = (): boolean => {
  const el = document.activeElement
  if (!el) return false
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea') return true
  if (el.getAttribute('contenteditable') === 'true') return true
  return false
}

const isPrintableKey = (e: KeyboardEvent): boolean => {
  if (e.ctrlKey || e.metaKey || e.altKey) return false
  if (!e.key || e.key.length !== 1) return false
  const special = ['Enter', 'Tab', 'Escape', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
  return !special.includes(e.key)
}

interface ConversationInputProps {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  onKeyPress: (e: React.KeyboardEvent) => void
  disabled?: boolean
  placeholder?: string
  showImageUpload?: boolean
  allowVideo?: boolean
  allowDocuments?: boolean
  hasPendingImage?: boolean
  imageUploadAcknowledged?: boolean
  onImageSelect?: (file: File) => void
  onImageClear?: () => void
  pendingImagePreview?: string | null
  pendingAttachmentMime?: string | null
  readyToExecute?: boolean
  onConfirmExecution?: () => void
  showDraftPublish?: boolean
  draftPublishing?: boolean
  onPublishDraft?: () => void
  onOpenHistory?: () => void
  historyCount?: number
}

const ConversationInput: React.FC<ConversationInputProps> = ({
  value,
  onChange,
  onSend,
  onKeyPress,
  disabled = false,
  placeholder = 'Escribí tu mensaje...',
  showImageUpload = false,
  allowVideo = false,
  allowDocuments = false,
  hasPendingImage = false,
  imageUploadAcknowledged = false,
  onImageSelect,
  onImageClear,
  pendingImagePreview = null,
  pendingAttachmentMime = null,
  readyToExecute = false,
  onConfirmExecution,
  showDraftPublish = false,
  draftPublishing = false,
  onPublishDraft,
  onOpenHistory,
  historyCount
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const valueRef = useRef(value)
  const onChangeRef = useRef(onChange)
  valueRef.current = value
  onChangeRef.current = onChange

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (disabled) return
      if (isEditableFocused()) return
      if (!isPrintableKey(e)) return

      e.preventDefault()
      textareaRef.current?.focus()
      onChangeRef.current(valueRef.current + e.key)
    }

    document.addEventListener('keydown', handleKeyDown, { capture: true })
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true })
  }, [disabled])

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && onImageSelect) {
      onImageSelect(file)
    }
    e.target.value = ''
  }

  const canSend = value.trim() || hasPendingImage

  const handleSendButtonClick = () => {
    onSend()
  }

  const showPdfAttachmentPreview =
    pendingAttachmentMime === 'application/pdf' ||
    (pendingImagePreview?.startsWith('data:application/pdf') ?? false)
  const showHistory =
    typeof onOpenHistory === 'function' &&
    typeof historyCount === 'number' &&
    historyCount > 0
  const historyLabel = historyCount && historyCount > 99 ? '99+' : String(historyCount ?? '')

  return (
    <div className={styles.inputWrapper}>
      <div className={styles.inputContainer}>
        {showImageUpload && (
          hasPendingImage && !imageUploadAcknowledged && pendingImagePreview ? (
            <div
              className={styles.pendingImagePreview}
              aria-label={showPdfAttachmentPreview ? 'PDF listo para enviar' : undefined}
            >
              {showPdfAttachmentPreview ? (
                <div className={styles.pendingPdfIcon}>
                  <FileText size={20} strokeWidth={1.85} aria-hidden />
                </div>
              ) : pendingImagePreview.startsWith('data:video/') ? (
                <video src={pendingImagePreview} controls />
              ) : (
                <img src={pendingImagePreview} alt="Vista previa" />
              )}
              <button
                type="button"
                className={styles.clearImageBtn}
                onClick={onImageClear}
                aria-label="Quitar imagen"
              >
                <X size={12} />
              </button>
            </div>
          ) : !hasPendingImage ? (
            <label className={styles.imageUploadBtn} title={allowVideo ? "Agregar imagen o video" : allowDocuments ? "Agregar archivo" : "Agregar imagen"} aria-label={allowVideo ? "Agregar imagen o video" : allowDocuments ? "Agregar archivo" : "Agregar imagen"}>
              <ImagePlus size={20} strokeWidth={1.8} />
              <input
                ref={fileInputRef}
                type="file"
                accept={allowVideo ? "image/jpeg,image/png,image/webp,video/mp4,video/webm" : allowDocuments ? "application/pdf,image/jpeg,image/png,image/webp" : "image/jpeg,image/png,image/webp"}
                onChange={handleImageChange}
                disabled={disabled}
              />
            </label>
          ) : null
        )}
        <textarea
          ref={textareaRef}
          className={styles.input}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
        />
        {showHistory && (
          <button
            type="button"
            className={styles.historyButton}
            onClick={onOpenHistory}
            aria-label="Ver conversaciones anteriores"
            title="Historial de conversaciones"
          >
            <MessageSquare size={18} />
            <span className={styles.historyBadge} aria-hidden="true">
              {historyLabel}
            </span>
          </button>
        )}
        {showDraftPublish && onPublishDraft ? (
          <button
            type="button"
            className={draftPublishStyles.draftPublishButton}
            onClick={onPublishDraft}
            disabled={disabled || draftPublishing}
            aria-label="Publicar obra"
          >
            {draftPublishing ? (
              <span>Publicando...</span>
            ) : (
              <>
                <Rocket size={18} strokeWidth={1.8} aria-hidden />
                <span>Publicar obra</span>
              </>
            )}
          </button>
        ) : null}
        {readyToExecute && onConfirmExecution ? (
          <button
            className={styles.confirmButton}
            onClick={onConfirmExecution}
            disabled={disabled}
            type="button"
            aria-label="Confirmar creación"
          >
            <Check size={18} />
            <span>Confirmar</span>
          </button>
        ) : (
          <button
            className={styles.sendButton}
            onClick={handleSendButtonClick}
            disabled={disabled || !canSend}
            type="button"
            aria-label="Enviar mensaje"
          >
            <SendHorizonal size={18} />
          </button>
        )}
      </div>
    </div>
  )
}

export default ConversationInput
